import { NgTemplateOutlet } from '@angular/common';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  effect,
  computed,
  model,
  viewChildren,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectComponent } from '../select/select.component';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { DateRangeComponent } from '../date-range/date-range.component';
import { PaginatedSelectComponent } from '../paginated-select/paginated-select.component';
import { TippyTooltipDirective } from '../tooltip/tippy-tooltip.directive';
import { ResourceTypeComponent } from '../resource-type/resource-type.component';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import {
  FilterOption,
  FilterValue,
  DateRangeKeys,
} from './filter-dropdown.model';
import { FilterDropdownService } from './filter-dropdown.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { PreloadedMultiSelectResponse } from '../../../core/common/models/api-response.model';
import { filterInputTypesEnum } from '../../enums/filter-input-type';
import { LookupOptionItem } from '../lookup-field/lookup-field-item';
import { OptionItem } from '../option.interface';

@Component({
  selector: 'lib-filter-dropdown',
  imports: [
    NgTemplateOutlet,
    TippyTooltipDirective,
    ReactiveFormsModule,
    SelectComponent,
    PaginatedSelectComponent,
    MultiSelectComponent,
    DateRangeComponent,
    ResourceTypeComponent,
    ClickOutSideDirective,
    TranslocoPipe,
  ],
  templateUrl: './filter-dropdown.component.html',
  styleUrl: './filter-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilterDropdownComponent implements OnInit, OnDestroy {
  private filterService = inject(FilterDropdownService);
  private readonly featureFlags = inject(FeatureFlagsService, {
    optional: true,
  });
  /** Tables_new_design: a bordered "Filters" button that sits in the table toolbar. */
  protected readonly isNewTableUi = signal(
    this.featureFlags?.getFeatureFlagValue(
      FEATURE_FLAGS.TABLE.ui_enhancement
    ) ?? false
  );

  filterOptions = model.required<FilterOption[]>();
  initialValues = input<FilterValue>({});
  applyFilters = output<FilterValue>();
  /** Emitted before applyFilters when "Clear all" was used, so hosts can also clear their chips filters. */
  clearAll = output<void>();
  private clearAllRequested = false;

  /** Resource type filters keep their own internal state, so they are cleared directly. */
  private readonly resourceTypeFilters = viewChildren(ResourceTypeComponent);

  isFilterOpen = signal<boolean>(false);
  filterForm!: FormGroup;
  dateRangesAppliedInComponent = signal<Set<string>>(new Set());
  hasFormChanged = signal<boolean>(false);
  preloadedMultiSelectResponse = signal<
    Record<string, PreloadedMultiSelectResponse>
  >({});
  private formSubscription?: Subscription;

  hasSelectedFilters = this.filterService.hasSelectedFilters;
  selectedFiltersCount = this.filterService.selectedFiltersCount;

  processedFilterOptions = computed(() => {
    return this.filterOptions().map(option => ({
      ...option,
      paginatedFilters: this.getPaginatedFilters(option),
      preloadedResponse:
        this.preloadedMultiSelectResponse()[option.key] ?? null,
      initialSelectedOptions: this.selectedMultiOptions()[option.key] ?? [],
    }));
  });

  selectedPaginatedOptions = signal<Record<string, LookupOptionItem>>({});
  selectedMultiOptions = signal<Record<string, OptionItem[]>>({});

  constructor() {
    effect(() => {
      const initialValues = this.initialValues();
      if (this.filterForm && Object.keys(this.filterForm.controls).length > 0) {
        this.filterService.updateFormFromInitialValues(
          this.filterForm,
          this.filterOptions(),
          initialValues
        );

        // Update date ranges applied in component based on initial values
        const appliedRanges = new Set<string>();
        this.filterOptions().forEach(option => {
          if (option.type === 'dateRange') {
            const { startKey, endKey } =
              this.filterService.getDateRangeKeys(option);
            const startValue = initialValues[startKey];
            const endValue = initialValues[endKey];

            // If both dates exist in initial values, mark as applied in component
            const hasStartValue =
              startValue !== null &&
              startValue !== undefined &&
              startValue !== '';
            const hasEndValue =
              endValue !== null && endValue !== undefined && endValue !== '';
            if (hasStartValue && hasEndValue) {
              appliedRanges.add(option.key);
            }
          }
        });
        this.dateRangesAppliedInComponent.set(appliedRanges);
      }
    });
  }

  ngOnInit() {
    this.filterForm = this.filterService.createFilterForm(
      this.filterOptions(),
      this.initialValues()
    );

    // Subscribe to form value changes
    this.formSubscription = this.filterForm.valueChanges.subscribe(() => {
      this.hasFormChanged.set(
        this.filterService.hasFormFilters(this.filterForm)
      );
    });

    // Preload multi-select options from API
    this.preloadMultiSelectOptions();

    // handle paginated selected options
    this.handleInitialPaginatedSelectedOptions();

    // handle multi-select selected options
    this.handleInitialMultiSelectedOptions();
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
  }

  toggleFilter() {
    const wasOpen = this.isFilterOpen();
    this.isFilterOpen.update(value => !value);

    // When opening the filter, reset form to applied filters
    if (!wasOpen) {
      this.filterService.resetFormToAppliedFilters(this.filterForm);
      this.filterService.storeOriginalFormState(this.filterForm);
      this.hasFormChanged.set(false);
    }
  }

  onApplyFilters() {
    const filters = this.filterService.applyFilters(this.filterForm);
    // Mark date ranges as applied when main Apply button is clicked
    this.filterService.markDateRangesAsApplied(
      this.filterForm,
      this.filterOptions()
    );
    if (this.clearAllRequested) {
      this.clearAllRequested = false;
      this.clearAll.emit();
    }
    this.applyFilters.emit(filters);
    this.hasFormChanged.set(false);
    this.isFilterOpen.set(false);
  }

  onCancelFilters() {
    if (!this.isFilterOpen()) return;
    this.clearAllRequested = false;
    // Reset form to applied filters and clear unapplied changes
    this.resetFormToAppliedState();
    this.hasFormChanged.set(false);
    this.isFilterOpen.set(false);
  }

  private resetFormToAppliedState() {
    this.filterService.cancelFilters(this.filterForm);

    // Reset date ranges applied in component to match applied filters
    const appliedRanges = new Set<string>();
    this.filterOptions().forEach(option => {
      if (option.type === 'dateRange') {
        const { startKey, endKey } =
          this.filterService.getDateRangeKeys(option);
        const startValue = this.filterForm.get(startKey)?.value;
        const endValue = this.filterForm.get(endKey)?.value;
        const hasStartValue =
          startValue !== null && startValue !== undefined && startValue !== '';
        const hasEndValue =
          endValue !== null && endValue !== undefined && endValue !== '';
        if (hasStartValue && hasEndValue) {
          appliedRanges.add(option.key);
        }
      }
    });
    this.dateRangesAppliedInComponent.set(appliedRanges);
  }

  resetAllFilters() {
    this.clearAllRequested = true;
    // Clear all date ranges applied in component
    this.dateRangesAppliedInComponent.set(new Set());
    this.selectedMultiOptions.set({});
    this.filterService.resetAllFilters(this.filterForm, this.filterOptions());
    this.resourceTypeFilters().forEach(filter => filter.clearFilter());
  }

  resetFilter(key: string) {
    const filterOption = this.filterOptions().find(opt => opt.key === key);

    // If resetting a date range, also remove it from applied in component
    if (filterOption?.type === 'dateRange') {
      this.dateRangesAppliedInComponent.update(ranges => {
        const newRanges = new Set(ranges);
        newRanges.delete(key);
        return newRanges;
      });
    }

    // If resetting a multi-select, also remove stored options
    if (filterOption?.type === 'multi') {
      this.selectedMultiOptions.update(current => {
        const updated = { ...current };
        delete updated[key];
        return updated;
      });
      this.filterService.selectedMultiOptionsSignal.set({
        ...this.selectedMultiOptions(),
      });
    }

    this.filterService.resetFilter(this.filterForm, this.filterOptions(), key);
  }

  isLastOption(index: number): boolean {
    return index === this.filterOptions().length - 1;
  }

  getDateRangeKeys(option: FilterOption): DateRangeKeys {
    return this.filterService.getDateRangeKeys(option);
  }

  getStartDateKey(option: FilterOption): string {
    return this.getDateRangeKeys(option).startKey;
  }

  getEndDateKey(option: FilterOption): string {
    return this.getDateRangeKeys(option).endKey;
  }

  hasFilterValue(key: string): boolean {
    const filterOption = this.filterOptions().find(opt => opt.key === key);

    // For date range, check if it has been applied within the date-range component
    if (filterOption?.type === 'dateRange') {
      return this.dateRangesAppliedInComponent().has(key);
    }

    return this.filterService.hasFilterValue(
      this.filterForm,
      this.filterOptions(),
      key
    );
  }

  onDateRangeAppliedInComponent(key: string) {
    this.dateRangesAppliedInComponent.update(ranges => {
      const newRanges = new Set(ranges);
      newRanges.add(key);
      return newRanges;
    });
  }

  hasFormFilters(): boolean {
    return this.hasFormChanged();
  }

  appliedFiltersCount(): number {
    return this.filterService.appliedFiltersCount(
      this.filterForm,
      this.filterOptions(),
      this.dateRangesAppliedInComponent()
    );
  }

  private getPaginatedFilters(filterOption: FilterOption) {
    const filters = filterOption.filters || [];
    const hasSearchFilter = filters.some(filter => filter.key === 'search');

    if (!hasSearchFilter && filterOption.hasSearch !== false) {
      return [
        ...filters,
        {
          key: 'search',
          value: '',
          value_type: filterInputTypesEnum.STATIC,
          multiple: false,
        },
      ];
    }

    return filters;
  }

  private preloadMultiSelectOptions() {
    this.filterOptions().forEach(option => {
      if (option.type === 'multi' && option.apiUrl) {
        this.filterService
          .loadMultiSelectOptions(option.apiUrl, 1, option.filters || [])
          .subscribe({
            next: result => {
              this.preloadedMultiSelectResponse.update(current => ({
                ...current,
                [option.key]: result,
              }));
            },
          });
      }
    });
  }

  onPaginatedOptionChange(key: string, selectedOption: LookupOptionItem) {
    this.selectedPaginatedOptions.update(current => ({
      ...current,
      [key]: { value: selectedOption.value, label: selectedOption.label },
    }));
    // update the selected paginated options signal
    this.filterService.selectedPaginatedOptionsSignal.set({
      ...this.selectedPaginatedOptions(),
    });
  }

  onMultiOptionChange(key: string, selectedOptions: OptionItem[]) {
    const selectedValues = selectedOptions.map(option => ({
      value: option.value || option.id,
      label: option.label || option.name,
    }));

    this.selectedMultiOptions.update(current => ({
      ...current,
      [key]: selectedValues,
    }));

    // update the selected multi options signal in the service
    this.filterService.selectedMultiOptionsSignal.set({
      ...this.selectedMultiOptions(),
    });
  }

  getSelectedPaginatedOption(key: string): LookupOptionItem {
    return this.selectedPaginatedOptions()[key] ?? undefined;
  }

  onResourceTypeChange(
    key: string,
    filter: {
      resourceType: string;
      assigneeId: (string | number)[];
      assigneeOptions: { value: string | number; label: string }[];
    }
  ) {
    // Store the resource type filter as a combined value
    this.filterForm.patchValue({
      [key]: JSON.stringify(filter),
    });
  }

  onResourceTypeReset(key: string) {
    this.filterForm.patchValue({
      [key]: '',
    });
  }

  private handleInitialPaginatedSelectedOptions() {
    const selectedPaginatedOptions =
      this.initialValues()[
        this.filterService.selectedPaginatedOptionsParamsKey
      ];
    if (selectedPaginatedOptions) {
      this.selectedPaginatedOptions.set({
        ...(selectedPaginatedOptions as unknown as Record<
          string,
          LookupOptionItem
        >),
      });
      this.filterService.selectedPaginatedOptionsSignal.set({
        ...this.selectedPaginatedOptions(),
      });
    }
  }

  private handleInitialMultiSelectedOptions() {
    const selectedMultiOptions =
      this.initialValues()[this.filterService.selectedMultiOptionsParamsKey];
    if (selectedMultiOptions) {
      this.selectedMultiOptions.set({
        ...(selectedMultiOptions as unknown as Record<string, OptionItem[]>),
      });
      this.filterService.selectedMultiOptionsSignal.set({
        ...this.selectedMultiOptions(),
      });
    }
  }
}
